import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Link,
  Flex,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const TermsOfUsePage: React.FC = () => {
  return (
    <Box minH="100vh" bg="white" color="purple.900">
      {/* Navigation */}
      <Box as="nav" py={6} px={8} position="fixed" top={0} left={0} right={0} zIndex={1000} bg="white" backdropFilter="blur(10px)" borderBottom="1px solid" borderColor="purple.200">
        <Container maxW="7xl">
          <Flex justify="space-between" align="center">
            <RouterLink to="/">
              <Link>
                <Box>
                  <Heading size="xl" color="purple.500" fontWeight="bold">
                    IDK
                  </Heading>
                  <Text fontSize="sm" color="purple.600" mt={-1}>
                    I Don't Know
                  </Text>
                </Box>
              </Link>
            </RouterLink>
            <HStack gap={8}>
              <RouterLink to="/">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontWeight="medium">
                  Home
                </Link>
              </RouterLink>
              <RouterLink to="/support">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontWeight="medium">
                  Support
                </Link>
              </RouterLink>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Box pt={32} pb={20}>
        <Container maxW="4xl">
          <VStack gap={12} align="start">
            {/* Header */}
            <VStack gap={6} align="start" w="full">
              <Heading size="3xl" fontWeight="bold" color="purple.900">
                Terms of Use
              </Heading>
              <Text fontSize="lg" color="purple.700">
                Last updated: January 2025
              </Text>
              <Box w="full" h="1px" bg="purple.200" />
            </VStack>

            {/* Introduction */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Agreement to Terms
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                Welcome to IDK ("we," "our," or "us"). These Terms of Use ("Terms") govern your access to and use of our mobile application and related services (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.
              </Text>
            </VStack>

            {/* Eligibility */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Eligibility
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                You must be at least 13 years of age to use our Service. By using the Service, you represent and warrant that you are at least 13 years old and have the legal capacity to enter into these Terms. If you are under 18, you represent that you have your parent's or guardian's permission to use the Service.
              </Text>
            </VStack>

            {/* Account Registration */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Account Registration
              </Heading>
              <VStack gap={2} align="start" w="full">
                <Text fontSize="md" color="purple.700">• You may be required to create an account to access certain features of the Service</Text>
                <Text fontSize="md" color="purple.700">• You are responsible for maintaining the confidentiality of your account credentials</Text>
                <Text fontSize="md" color="purple.700">• You agree to provide accurate, current, and complete information during registration</Text>
                <Text fontSize="md" color="purple.700">• You are responsible for all activities that occur under your account</Text>
                <Text fontSize="md" color="purple.700">• You must notify us immediately of any unauthorized use of your account</Text>
              </VStack>
            </VStack>

            {/* Subscription Services */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Subscription Services
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall" mb={4}>
                Our Service offers premium subscription plans that provide access to additional features and content:
              </Text>
              <VStack gap={2} align="start" w="full">
                <Text fontSize="md" color="purple.700">• Subscriptions are billed on a recurring basis (monthly or yearly) as selected by you</Text>
                <Text fontSize="md" color="purple.700">• Subscription fees are charged to your payment method on file</Text>
                <Text fontSize="md" color="purple.700">• Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period</Text>
                <Text fontSize="md" color="purple.700">• You may cancel your subscription at any time through your device settings or app store account</Text>
                <Text fontSize="md" color="purple.700">• Refunds are subject to the policies of the app store from which you purchased the subscription</Text>
                <Text fontSize="md" color="purple.700">• We reserve the right to change subscription prices with reasonable notice</Text>
              </VStack>
            </VStack>

            {/* User Conduct */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                User Conduct
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall" mb={4}>
                You agree not to:
              </Text>
              <VStack gap={2} align="start" w="full">
                <Text fontSize="md" color="purple.700">• Use the Service for any illegal or unauthorized purpose</Text>
                <Text fontSize="md" color="purple.700">• Violate any applicable laws or regulations</Text>
                <Text fontSize="md" color="purple.700">• Interfere with or disrupt the Service or servers</Text>
                <Text fontSize="md" color="purple.700">• Attempt to gain unauthorized access to any part of the Service</Text>
                <Text fontSize="md" color="purple.700">• Transmit any viruses, malware, or harmful code</Text>
                <Text fontSize="md" color="purple.700">• Use automated systems to access the Service without permission</Text>
                <Text fontSize="md" color="purple.700">• Impersonate any person or entity</Text>
                <Text fontSize="md" color="purple.700">• Collect or store personal data about other users</Text>
              </VStack>
            </VStack>

            {/* Intellectual Property */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Intellectual Property Rights
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                The Service, including its original content, features, and functionality, is owned by IDK and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any part of the Service without our prior written permission.
              </Text>
            </VStack>

            {/* Disclaimer of Warranties */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Disclaimer of Warranties
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </Text>
            </VStack>

            {/* Limitation of Liability */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Limitation of Liability
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IDK SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
              </Text>
            </VStack>

            {/* Indemnification */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Indemnification
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                You agree to indemnify, defend, and hold harmless IDK and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.
              </Text>
            </VStack>

            {/* Termination */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Termination
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will cease immediately. All provisions of these Terms that by their nature should survive termination shall survive termination.
              </Text>
            </VStack>

            {/* Changes to Terms */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Changes to Terms
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. Your continued use of the Service after any such changes constitutes your acceptance of the new Terms.
              </Text>
            </VStack>

            {/* Governing Law */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Governing Law
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in the United States.
              </Text>
            </VStack>

            {/* Contact Information */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Contact Us
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                If you have any questions about these Terms of Use, please contact us at:
              </Text>
              <VStack gap={2} align="start">
                <Text fontSize="md" color="purple.700">Email: legal@idkapp.com</Text>
                <Text fontSize="md" color="purple.700">Support: support@idkapp.com</Text>
                <Text fontSize="md" color="purple.700">Website: <Link href="/support" color="purple.500">www.idkapp.com/support</Link></Text>
              </VStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box py={12} bg="purple.50" borderTop="1px solid" borderColor="purple.200">
        <Container maxW="7xl">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Text color="purple.600" fontSize="sm">
              © 2025 IDK App. All rights reserved.
            </Text>
            <HStack gap={6}>
              <RouterLink to="/">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontSize="sm">
                  Home
                </Link>
              </RouterLink>
              <RouterLink to="/support">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontSize="sm">
                  Support
                </Link>
              </RouterLink>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsOfUsePage;

